import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import AdminAccountingRepository from '../../services/AdminAccountingRepository.js';
import { can } from '../../config/adminPermissions.js';
import LoadingSpinner from '../common/LoadingSpinner.jsx';
import { devError } from '../../utils/logger.js';
import {
  compareYearMonth,
  formatYearMonthJa,
  shiftYearMonth,
} from '../../utils/accountingMonthUtils.js';
import { useScreenProbe } from '../../utils/screenTiming.js';
import './AdminAccounting.css';

const FILE_CONFIG = {
  sales: {
    label: '顧客別・製品別売上',
    shortLabel: '売上一覧',
    countKey: 'sales',
  },
  payments: {
    label: 'Stripe 決済明細',
    shortLabel: '決済明細',
    countKey: 'payments',
  },
  payoutsCsv: {
    label: '銀行入金明細 CSV',
    shortLabel: '入金 CSV',
    countKey: 'payouts',
  },
  payoutsPdf: {
    label: '銀行入金明細 PDF',
    shortLabel: '入金 PDF',
    countKey: 'payouts',
  },
  refunds: {
    label: '返金一覧',
    shortLabel: '返金',
    countKey: 'refunds',
  },
  qualifiedInvoicesCsv: {
    label: '適格請求書一覧',
    shortLabel: '適格一覧',
    countKey: 'qualifiedInvoices',
  },
  qualifiedInvoicesZip: {
    label: '適格請求書 PDF 一式',
    shortLabel: '適格 PDF',
    countKey: 'qualifiedInvoices',
  },
};

const STATUS_LABELS = {
  generated: '生成済み',
  uploaded: 'Drive 送信済み',
  generated_drive_skipped: '生成済み（Drive 未設定）',
  generated_drive_failed: '生成済み（Drive 送信失敗）',
};

const PAGE_SIZE = 50;

function formatMoney(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return value || '—';
  return `${new Intl.NumberFormat('ja-JP').format(number)} 円`;
}

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

function StatusBadge({ status }) {
  return (
    <span className={`accounting-status status-${status || 'unknown'}`}>
      <span className="accounting-status-dot" aria-hidden="true" />
      {STATUS_LABELS[status] || status || '状態不明'}
    </span>
  );
}

function InlineSpinner() {
  return <span className="accounting-inline-spinner" aria-hidden="true" />;
}

function SummaryCards({ preview }) {
  const totals = preview?.summary?.totals || {};
  const totalEntries = Object.entries(totals).slice(0, 3);
  return (
    <div className="accounting-summary-strip" aria-label="表示データの集計">
      <div className="accounting-summary-item">
        <span>該当件数</span>
        <strong>{new Intl.NumberFormat('ja-JP').format(preview?.summary?.rowCount || 0)} 件</strong>
      </div>
      {totalEntries.map(([label, value]) => (
        <div key={label} className="accounting-summary-item">
          <span>{label} 合計</span>
          <strong>{formatMoney(value)}</strong>
        </div>
      ))}
    </div>
  );
}

function ReportTable({ preview, loading }) {
  if (!preview && loading) {
    return (
      <div className="accounting-table-loading" role="status">
        <InlineSpinner />
        表データを取得しています…
      </div>
    );
  }
  if (!preview?.rows?.length) {
    return (
      <div className="accounting-table-empty">
        <span className="accounting-empty-icon" aria-hidden="true">0</span>
        <strong>該当データはありません</strong>
        <p>この形式で表示できる明細は0件です。</p>
      </div>
    );
  }

  return (
    <div className={`accounting-table-wrap${loading ? ' is-loading' : ''}`}>
      {loading && (
        <div className="accounting-table-overlay" role="status">
          <InlineSpinner />
          更新中…
        </div>
      )}
      <table className="accounting-data-table">
        <thead>
          <tr>
            {preview.columns.map((column) => (
              <th
                key={column.key}
                className={`column-${column.type}`}
                scope="col"
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {preview.rows.map((row, rowIndex) => (
            <tr key={`${preview.pagination.page}-${rowIndex}`}>
              {preview.columns.map((column) => {
                const raw = row[column.key];
                const value = column.type === 'money' ? formatMoney(raw) : (raw || '—');
                return (
                  <td
                    key={column.key}
                    className={`column-${column.type}`}
                    title={String(raw || '')}
                  >
                    {column.type === 'status'
                      ? <span className="accounting-cell-badge">{value}</span>
                      : value}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Stripe明細（Stripe レポート閲覧・出力・税設定・Drive 連携）
 */
const AdminAccounting = ({ user }) => {
  const probe = useScreenProbe('accounting');
  const repo = AdminAccountingRepository.getInstance();
  const requestIdRef = useRef(0);
  const previewRequestIdRef = useRef(0);
  const previewCacheRef = useRef(new Map());

  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [yearMonth, setYearMonth] = useState('');
  const [selectedRun, setSelectedRun] = useState(null);
  const [activeTab, setActiveTab] = useState('sales');
  const [preview, setPreview] = useState(null);
  const [page, setPage] = useState(1);
  const [initialLoading, setInitialLoading] = useState(true);
  const [monthLoading, setMonthLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [uploadToDrive, setUploadToDrive] = useState(true);
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState(null);
  const [migrateProductId, setMigrateProductId] = useState('');
  const [migrateLimit, setMigrateLimit] = useState('50');
  const [lastDryRun, setLastDryRun] = useState(null);

  const canWrite = can(user, 'accounting:write');
  const canExport = can(user, 'accounting:export');
  const maxYearMonth = status?.schedule?.defaultTargetYearMonth || '';

  const availableTabs = useMemo(() => (
    (selectedRun?.files || [])
      .filter((file) => FILE_CONFIG[file.key])
      .map((file) => ({
        ...file,
        ...FILE_CONFIG[file.key],
        count: selectedRun?.counts?.[FILE_CONFIG[file.key].countKey] ?? 0,
      }))
  ), [selectedRun]);

  const activeFile = availableTabs.find((tab) => tab.key === activeTab) || null;

  const refreshSupportingData = useCallback(async () => {
    const [statusRes, runList] = await Promise.all([
      repo.getStatus(),
      repo.listRuns(24),
    ]);
    setStatus(statusRes);
    setRuns(runList);
    return statusRes;
  }, [repo]);

  const loadMonth = useCallback(async (targetMonth, { announce = false } = {}) => {
    if (!targetMonth) return;
    const requestId = ++requestIdRef.current;
    probe.start('month');
    setMonthLoading(true);
    setNotice(null);
    try {
      const run = await repo.getLatestRun(targetMonth);
      if (requestId !== requestIdRef.current) {
        probe.end('month', 'stale');
        return;
      }
      setSelectedRun(run);
      setPage(1);
      setPreview(null);
      const keys = (run?.files || []).map((file) => file.key);
      setActiveTab((current) => (keys.includes(current) ? current : (keys[0] || 'sales')));
      if (announce) {
        setNotice({
          type: 'info',
          text: run
            ? `${formatYearMonthJa(targetMonth)}の最新レポートを取得しました。`
            : `${formatYearMonthJa(targetMonth)}のレポートはまだ生成されていません。`,
        });
      }
      probe.end('month', 'ok');
      probe.unlock();
    } catch (err) {
      if (requestId !== requestIdRef.current) {
        probe.end('month', 'stale');
        return;
      }
      probe.end('month', `error: ${err?.message || 'unknown'}`);
      probe.unlock();
      devError('月次会計レポート取得エラー', err);
      setSelectedRun(null);
      setPreview(null);
      setNotice({ type: 'error', text: err.message || '最新レポートの取得に失敗しました。' });
    } finally {
      if (requestId === requestIdRef.current) setMonthLoading(false);
    }
  }, [repo, probe]);

  useEffect(() => {
    let active = true;
    const initialize = async () => {
      probe.start('init');
      setInitialLoading(true);
      try {
        const statusRes = await refreshSupportingData();
        if (!active) return;
        const ym = statusRes?.schedule?.defaultTargetYearMonth || '';
        setYearMonth(ym);
        probe.end('init', 'ok');
        if (!ym) probe.unlock();
      } catch (err) {
        if (!active) return;
        probe.end('init', `error: ${err?.message || 'unknown'}`);
        probe.unlock();
        devError('会計画面初期化エラー', err);
        setNotice({ type: 'error', text: err.message || '会計情報の取得に失敗しました。' });
      } finally {
        if (active) setInitialLoading(false);
      }
    };
    initialize();
    return () => {
      active = false;
      requestIdRef.current += 1;
      previewRequestIdRef.current += 1;
    };
  }, [refreshSupportingData, probe]);

  useEffect(() => {
    if (!yearMonth || initialLoading) return;
    loadMonth(yearMonth);
  }, [yearMonth, initialLoading, loadMonth]);

  useEffect(() => {
    if (!selectedRun?.id || !activeTab) {
      setPreview(null);
      return;
    }
    const cacheKey = `${selectedRun.id}:${activeTab}:${page}:${PAGE_SIZE}`;
    const cached = previewCacheRef.current.get(cacheKey);
    if (cached) {
      setPreview(cached);
      setPreviewLoading(false);
      return;
    }

    const requestId = ++previewRequestIdRef.current;
    setPreviewLoading(true);
    repo.getPreview(selectedRun.id, activeTab, { page, pageSize: PAGE_SIZE })
      .then((result) => {
        if (requestId !== previewRequestIdRef.current) return;
        previewCacheRef.current.set(cacheKey, result);
        setPreview(result);
      })
      .catch((err) => {
        if (requestId !== previewRequestIdRef.current) return;
        devError('会計表プレビュー取得エラー', err);
        setPreview(null);
        setNotice({ type: 'error', text: err.message || '表データの取得に失敗しました。' });
      })
      .finally(() => {
        if (requestId === previewRequestIdRef.current) setPreviewLoading(false);
      });
  }, [activeTab, page, repo, selectedRun]);

  const moveMonth = (delta) => {
    const next = shiftYearMonth(yearMonth, delta);
    if (!next || (maxYearMonth && compareYearMonth(next, maxYearMonth) > 0)) return;
    setYearMonth(next);
  };

  const handleMonthInput = (event) => {
    const next = event.target.value;
    if (maxYearMonth && compareYearMonth(next, maxYearMonth) > 0) return;
    setYearMonth(next);
  };

  const selectHistoryMonth = (targetMonth) => {
    if (!targetMonth) return;
    if (maxYearMonth && compareYearMonth(targetMonth, maxYearMonth) > 0) {
      setNotice({
        type: 'warning',
        text: `${formatYearMonthJa(targetMonth)}は既定の対象月より先のため開けません。`,
      });
      return;
    }
    setYearMonth(targetMonth);
  };

  const handleTabChange = (fileKey) => {
    setActiveTab(fileKey);
    setPage(1);
    setNotice(null);
  };

  const handleGenerate = async () => {
    if (!canWrite || !yearMonth) return;
    setBusy('generate');
    setNotice({ type: 'info', text: `${formatYearMonthJa(yearMonth)}のレポートを生成しています…` });
    try {
      const result = await repo.generate({
        yearMonth,
        uploadToDrive: uploadToDrive && canExport,
      });
      previewCacheRef.current.clear();
      await Promise.all([
        loadMonth(yearMonth),
        refreshSupportingData(),
      ]);
      setNotice({
        type: result.driveError ? 'warning' : 'success',
        text: result.driveError
          ? `レポートは生成されましたが、Drive 送信に失敗しました: ${result.driveError}`
          : `レポートを生成しました（${result.counts?.sales ?? 0}件の売上明細）。`,
      });
    } catch (err) {
      setNotice({ type: 'error', text: err.message || 'レポート生成に失敗しました。' });
    } finally {
      setBusy('');
    }
  };

  const handleDownloadCurrent = async () => {
    if (!canExport || !selectedRun || !activeFile) return;
    setBusy('download-current');
    setNotice(null);
    try {
      await repo.downloadFile(selectedRun.id, activeFile.key, activeFile.name);
      setNotice({ type: 'success', text: `${activeFile.label}を出力しました。` });
    } catch (err) {
      setNotice({ type: 'error', text: err.message || 'ファイル出力に失敗しました。' });
    } finally {
      setBusy('');
    }
  };

  const handleDownloadAll = async () => {
    if (!canExport || !selectedRun) return;
    setBusy('download-all');
    setNotice(null);
    try {
      await repo.downloadAll(selectedRun.id, selectedRun.yearMonth);
      setNotice({ type: 'success', text: '全形式を ZIP で一括出力しました。' });
    } catch (err) {
      setNotice({ type: 'error', text: err.message || '一括出力に失敗しました。' });
    } finally {
      setBusy('');
    }
  };

  const runWithLiveConfirm = async (actionLabel, apiCall) => {
    try {
      return await apiCall(false);
    } catch (err) {
      if (err.code === 'confirm-live-required') {
        if (!window.confirm(
          `現在 live モードです。本番 Stripe の${actionLabel}を実行します。本当に適用しますか？`,
        )) {
          setNotice({ type: 'info', text: 'live 適用をキャンセルしました。' });
          return null;
        }
        return apiCall(true);
      }
      throw err;
    }
  };

  const handleMigrateTax = async () => {
    if (!canWrite) return;
    if (!window.confirm(
      '有料製品の税別設定を整備し、必要なら同額の exclusive Price を再作成します（金額の換算は行いません）。続行しますか？',
    )) return;
    setBusy('migrate');
    setNotice({ type: 'info', text: '製品の税設定を更新しています…' });
    try {
      const result = await runWithLiveConfirm('製品税整備', (confirmLive) => (
        repo.migrateTax({
          recreatePrice: true,
          confirmLive,
          productId: migrateProductId.trim() || null,
        })
      ));
      if (!result) return;
      await refreshSupportingData();
      setNotice({
        type: 'success',
        text: `税設定を更新しました（更新 ${result.migrated}件／確認済み ${result.skipped}件`
          + `／未完了 ${result.incomplete || 0}件）。`,
      });
    } catch (err) {
      setNotice({ type: 'error', text: err.message || '税設定の更新に失敗しました。' });
    } finally {
      setBusy('');
    }
  };

  const handleSubTaxDryRun = async () => {
    if (!canWrite) return;
    setBusy('sub-tax-dry');
    setNotice({ type: 'info', text: '既存サブスクの税付与対象を走査しています…' });
    try {
      const report = await repo.dryRunSubscriptionTaxMigration({
        productId: migrateProductId.trim() || null,
        limit: Number(migrateLimit) || 0,
      });
      const c = report.counts || {};
      setLastDryRun(report);
      setNotice({
        type: 'success',
        text: `dry-run 完了（mode=${report.mode}）: A(OK)=${c.A || 0} / B(税のみ)=${c.B || 0}`
          + ` / C(Price+税)=${c.C || 0} / D(スキップ)=${c.D || 0}`
          + (migrateProductId.trim() ? ` / product=${migrateProductId.trim()}` : ''),
      });
    } catch (err) {
      setNotice({ type: 'error', text: err.message || 'dry-run に失敗しました。' });
    } finally {
      setBusy('');
    }
  };

  const handleSubTaxApply = async () => {
    if (!canWrite) return;
    const limit = Number(migrateLimit) || 0;
    if (!window.confirm(
      '既存サブスクへ税を後付けします（proration なし・即時請求なし・次回請求から税）。'
      + (migrateProductId.trim() ? `\n製品ID: ${migrateProductId.trim()}` : '\n製品フィルタ: なし（全製品）')
      + `\n件数上限: ${limit > 0 ? limit : 'なし'}`
      + '\nlive の場合は続けて確認が必要です。続行しますか？',
    )) return;
    setBusy('sub-tax-apply');
    setNotice({ type: 'info', text: '既存サブスクへ税を適用しています…' });
    try {
      const result = await runWithLiveConfirm('既存契約税後付け', (confirmLive) => (
        repo.migrateSubscriptionTax({
          dryRun: false,
          confirmLive,
          productId: migrateProductId.trim() || null,
          limit,
        })
      ));
      if (!result) return;
      setNotice({
        type: 'success',
        text: `適用完了（mode=${result.mode}）: 更新 ${result.appliedCount}件`
          + `／部分 ${result.partialCount || 0}件／スキップ ${result.skippedCount}件`
          + `／エラー ${result.errorCount}件`
          + (result.runId ? `（run=${result.runId}）` : ''),
      });
    } catch (err) {
      setNotice({ type: 'error', text: err.message || '既存サブスク税の適用に失敗しました。' });
    } finally {
      setBusy('');
    }
  };

  const handleDriveUpload = async (runId) => {
    if (!canExport) return;
    setBusy(`drive-${runId}`);
    setNotice(null);
    try {
      await repo.uploadDrive(runId);
      await refreshSupportingData();
      if (selectedRun?.id === runId) await loadMonth(yearMonth);
      setNotice({ type: 'success', text: 'Google Drive へアップロードしました。' });
    } catch (err) {
      setNotice({ type: 'error', text: err.message || 'Drive アップロードに失敗しました。' });
    } finally {
      setBusy('');
    }
  };

  if (initialLoading) {
    return (
      <div className="admin-accounting">
        <LoadingSpinner message="最新の会計情報を読み込み中…" />
      </div>
    );
  }

  const products = status?.products || {};
  const drive = status?.drive || {};
  const schedule = status?.schedule || {};
  const tax = status?.tax || {};
  const pagination = preview?.pagination;
  const nextDisabled = !yearMonth
    || !maxYearMonth
    || compareYearMonth(yearMonth, maxYearMonth) >= 0
    || monthLoading;

  return (
    <div className="admin-accounting">
      <div className="section-header accounting-page-header">
        <div>
          <h1>Stripe明細</h1>
          <p>Stripe の月次会計情報を確認し、CSV・PDF・ZIP で出力できます。</p>
        </div>
        <div className="accounting-drive-indicator">
          <span className={`accounting-connection-dot${drive.configured ? ' is-connected' : ''}`} />
          Google Drive {drive.configured ? '接続済み' : '未設定'}
        </div>
      </div>

      {notice && (
        <div
          className={`accounting-banner is-${notice.type}`}
          role={notice.type === 'error' ? 'alert' : 'status'}
          aria-live="polite"
        >
          <span className="accounting-banner-icon" aria-hidden="true">
            {notice.type === 'success' ? '✓' : notice.type === 'error' ? '!' : 'i'}
          </span>
          <span>{notice.text}</span>
          <button
            type="button"
            className="accounting-banner-close"
            onClick={() => setNotice(null)}
            aria-label="通知を閉じる"
          >
            ×
          </button>
        </div>
      )}

      <section
        className={`accounting-report-workspace${monthLoading ? ' is-month-loading' : ''}`}
        aria-busy={monthLoading}
      >
        <div className="accounting-month-toolbar">
          <button
            type="button"
            className="accounting-month-button"
            onClick={() => moveMonth(-1)}
            disabled={monthLoading}
            aria-label="前月へ移動"
          >
            <span aria-hidden="true">←</span>
            前月
          </button>

          <div className="accounting-month-center">
            <span className="accounting-month-kicker">対象月</span>
            <div className="accounting-month-title">
              {monthLoading && <InlineSpinner />}
              {formatYearMonthJa(yearMonth)}
            </div>
            <input
              type="month"
              value={yearMonth}
              max={maxYearMonth || undefined}
              onChange={handleMonthInput}
              disabled={monthLoading}
              aria-label="対象月を選択"
            />
          </div>

          <button
            type="button"
            className="accounting-month-button"
            onClick={() => moveMonth(1)}
            disabled={nextDisabled}
            aria-label="次月へ移動"
          >
            次月
            <span aria-hidden="true">→</span>
          </button>
        </div>

        <div className="accounting-report-header">
          <div className="accounting-run-context">
            {monthLoading ? (
              <>
                <InlineSpinner />
                <span>{formatYearMonthJa(yearMonth)}の最新レポートを取得中…</span>
              </>
            ) : selectedRun ? (
              <>
                <StatusBadge status={selectedRun.status} />
                <span>最新取得: {formatDateTime(selectedRun.createdAt || selectedRun.generatedAt)}</span>
                <span className="accounting-run-id">Run {selectedRun.id}</span>
              </>
            ) : (
              <span>この月のレポートはまだありません</span>
            )}
          </div>
          <div className="accounting-report-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => loadMonth(yearMonth, { announce: true })}
              disabled={monthLoading || Boolean(busy)}
            >
              {monthLoading ? <InlineSpinner /> : '↻'}
              再取得
            </button>
            {canWrite && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleGenerate}
                disabled={Boolean(busy) || monthLoading}
              >
                {busy === 'generate' && <InlineSpinner />}
                {selectedRun ? '最新情報で再生成' : 'レポートを生成'}
              </button>
            )}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleDownloadAll}
              disabled={!canExport || !selectedRun || Boolean(busy) || monthLoading}
              title={!canExport ? '一括出力権限が必要です' : ''}
            >
              {busy === 'download-all' && <InlineSpinner />}
              一括出力（ZIP）
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleDownloadCurrent}
              disabled={!canExport || !selectedRun || !activeFile || Boolean(busy) || monthLoading}
              title={!canExport ? '出力権限が必要です' : ''}
            >
              {busy === 'download-current' && <InlineSpinner />}
              この形式を出力
            </button>
          </div>
        </div>

        {canWrite && drive.configured && (
          <label className="accounting-drive-option">
            <input
              type="checkbox"
              checked={uploadToDrive && canExport}
              onChange={(event) => setUploadToDrive(event.target.checked)}
              disabled={!canExport || Boolean(busy)}
            />
            再生成時に Google Drive にも保存
          </label>
        )}

        {selectedRun && selectedRun.hasFiles === false && (
          <div className="accounting-banner is-warning" role="status">
            <span className="accounting-banner-icon" aria-hidden="true">i</span>
            <span>
              この実行の明細データは画面用に保存されていません。表表示やダウンロード時は Stripe から再構築します。必要なら「最新情報で再生成」を実行してください。
            </span>
          </div>
        )}

        {selectedRun?.qualifiedInvoiceIncomplete && (
          <div className="accounting-banner is-error" role="alert">
            <span className="accounting-banner-icon" aria-hidden="true">!</span>
            <span>
              適格請求書 PDF の取得に失敗した明細があります
              （Invoice 失敗 {selectedRun.qualifiedInvoiceStats?.invoicesFailed || 0} 件 /
              Credit Note 失敗 {selectedRun.qualifiedInvoiceStats?.creditNotesFailed || 0} 件）。
              「最新情報で再生成」で再取得してください。
              {Array.isArray(selectedRun.qualifiedInvoiceStats?.failures)
                && selectedRun.qualifiedInvoiceStats.failures.length > 0
                ? ` 失敗ID: ${selectedRun.qualifiedInvoiceStats.failures
                  .slice(0, 5)
                  .map((f) => f.stripeId || f.number || '?')
                  .join(', ')}${selectedRun.qualifiedInvoiceStats.failures.length > 5 ? ' …' : ''}`
                : ''}
            </span>
          </div>
        )}

        {monthLoading && !selectedRun ? (
          <div className="accounting-workspace-loading" role="status">
            <InlineSpinner />
            {formatYearMonthJa(yearMonth)}の最新レポートを取得しています…
          </div>
        ) : !selectedRun ? (
          <div className="accounting-no-report">
            <div className="accounting-no-report-icon" aria-hidden="true">＋</div>
            <h2>この月のレポートはまだありません</h2>
            <p>Stripe から {formatYearMonthJa(yearMonth)} の最新情報を取得してレポートを作成できます。</p>
            {canWrite && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleGenerate}
                disabled={Boolean(busy)}
              >
                {busy === 'generate' && <InlineSpinner />}
                レポートを生成
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="accounting-tabs" role="tablist" aria-label="レポート形式">
              {availableTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.key}
                  className={`accounting-tab${activeTab === tab.key ? ' is-active' : ''}`}
                  onClick={() => handleTabChange(tab.key)}
                  disabled={monthLoading}
                >
                  <span>{tab.shortLabel}</span>
                  <span className="accounting-tab-count">{tab.count}</span>
                </button>
              ))}
            </div>

            <div
              className="accounting-report-content"
              role="tabpanel"
              aria-label={activeFile?.label}
            >
              <div className="accounting-format-heading">
                <div>
                  <span className="accounting-format-label">表示中の形式</span>
                  <h2>{activeFile?.label || 'レポート'}</h2>
                  {activeTab === 'sales' && (
                    <p className="accounting-tax-impute-note">
                      税抜・税・税込は Stripe 上の税分離をそのまま反映します。「税徴収=実徴収」は Stripe が税を加算した請求、
                      「税徴収=未徴収」は税額 0 / not_collecting / 未設定の請求です（税込逆算の推計は行いません）。
                      実支払額が請求合計と異なる場合は顧客残高の繰越などが含まれるため、「実支払額」「残高調整額（実支払−請求合計）」列と ChargeID で決済明細と突合できます。
                      正の残高調整は追加請求（過去債務の回収）、負はクレジット充当です。実支払 0 円の paid Invoice（最低課金未満など）は売上一覧から除外します。
                    </p>
                  )}
                  {activeTab === 'payments' && (
                    <p className="accounting-tax-impute-note">
                      Type=charge / payment がカード・Link 等の実決済、Type=stripe_fee が Billing Usage Fee 等の別枠課金です。
                      決済の差引額合計と入金額の差は、主に stripe_fee と入金月のずれによります。SourceID（ch_/py_）は売上一覧の ChargeID と突合できます。
                    </p>
                  )}
                  {activeTab === 'payoutsPdf' && (
                    <p>PDF に収録される入金データを表形式で確認しています。</p>
                  )}
                  {(activeTab === 'payoutsCsv') && (
                    <p className="accounting-tax-impute-note">
                      入金は到着日（arrival_date）基準です。構成決済の発生月が入金月と異なる場合があり、同月の決済明細合計とは一致しないことがあります。
                    </p>
                  )}
                  {activeTab === 'qualifiedInvoicesCsv' && (
                    <p className="accounting-tax-impute-note">
                      Stripe が発行する適格請求書（Invoice）と適格返還請求書（Credit Note）の突合一覧です。
                      「ファイル名」列は PDF 一式 ZIP 内のパスと対応します。売上一覧の請求書番号 / InvoiceID と突合できます。
                    </p>
                  )}
                  {activeTab === 'qualifiedInvoicesZip' && (
                    <p className="accounting-tax-impute-note">
                      PDF 一式の内容を一覧表で確認しています。原本 PDF は「この形式を出力」または一括 ZIP から取得してください。
                    </p>
                  )}
                </div>
                <span className="accounting-file-name">{activeFile?.name}</span>
              </div>

              <SummaryCards preview={preview} />
              <ReportTable preview={preview} loading={previewLoading} />

              {pagination && pagination.totalRows > 0 && (
                <div className="accounting-pagination">
                  <span>
                    {pagination.totalRows}件中{' '}
                    {(pagination.page - 1) * pagination.pageSize + 1}
                    〜
                    {Math.min(pagination.page * pagination.pageSize, pagination.totalRows)}件
                  </span>
                  <div>
                    <button
                      type="button"
                      className="accounting-page-button"
                      disabled={pagination.page <= 1 || previewLoading}
                      onClick={() => setPage((current) => Math.max(1, current - 1))}
                    >
                      ← 前へ
                    </button>
                    <span>{pagination.page} / {pagination.totalPages}</span>
                    <button
                      type="button"
                      className="accounting-page-button"
                      disabled={pagination.page >= pagination.totalPages || previewLoading}
                      onClick={() => setPage((current) => current + 1)}
                    >
                      次へ →
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </section>

      <div className="accounting-grid accounting-settings-grid">
        <section className="accounting-panel">
          <div className="accounting-panel-title">
            <span className="accounting-panel-icon" aria-hidden="true">%</span>
            <div>
              <h2>税設定</h2>
              <p>Stripe Tax と税別価格の適用状況</p>
            </div>
          </div>
          <dl className="accounting-dl">
            <div>
              <dt>価格モード</dt>
              <dd><span className="accounting-good-badge">税別</span> {tax.taxRatePercent ?? 10}%</dd>
            </div>
            <div>
              <dt>Tax Rate ID</dt>
              <dd className="mono">{tax.japanTaxRateId || '未取得'}</dd>
            </div>
            <div>
              <dt>製品移行</dt>
              <dd>
                <strong>{products.taxConfiguredCount ?? 0}</strong> / {products.paidProductCount ?? 0} 製品
                {products.pendingMigration > 0 && (
                  <span className="accounting-warning-text"> 残り {products.pendingMigration}</span>
                )}
              </dd>
            </div>
          </dl>
          {status?.taxError && <p className="accounting-note is-error">Stripe: {status.taxError}</p>}
          {(tax.notes || []).map((note) => (
            <p key={note} className="accounting-note">{note}</p>
          ))}
          {canWrite && (
            <div className="accounting-tax-actions-block">
              <div className="accounting-migrate-filters">
                <label>
                  製品ID（任意）
                  <input
                    type="text"
                    value={migrateProductId}
                    onChange={(e) => setMigrateProductId(e.target.value)}
                    placeholder="空欄=全製品"
                    disabled={Boolean(busy)}
                  />
                </label>
                <label>
                  件数上限
                  <input
                    type="number"
                    min="0"
                    value={migrateLimit}
                    onChange={(e) => setMigrateLimit(e.target.value)}
                    disabled={Boolean(busy)}
                  />
                </label>
              </div>
              <div className="accounting-tax-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={Boolean(busy)}
                  onClick={handleMigrateTax}
                >
                  {busy === 'migrate' && <InlineSpinner />}
                  税別・税設定を整備
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={Boolean(busy)}
                  onClick={handleSubTaxDryRun}
                >
                  {busy === 'sub-tax-dry' && <InlineSpinner />}
                  既存契約 dry-run
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={Boolean(busy)}
                  onClick={handleSubTaxApply}
                >
                  {busy === 'sub-tax-apply' && <InlineSpinner />}
                  既存契約へ税を後付け
                </button>
              </div>
              {lastDryRun?.counts && (
                <p className="accounting-note">
                  直近 dry-run: A={lastDryRun.counts.A || 0} B={lastDryRun.counts.B || 0}
                  {' '}C={lastDryRun.counts.C || 0} D={lastDryRun.counts.D || 0}
                  {' '}(mode={lastDryRun.mode})
                </p>
              )}
            </div>
          )}
          <p className="accounting-note">
            既存契約は proration なし（即時請求なし）。税は次回請求から。live は error.code で二重確認。
            製品ID指定時は製品整備・既存契約の両方に適用。件数上限は走査上限（dry-run/apply 共通）。
          </p>
        </section>

        <section className="accounting-panel">
          <div className="accounting-panel-title">
            <span className="accounting-panel-icon" aria-hidden="true">⏱</span>
            <div>
              <h2>自動出力スケジュール</h2>
              <p>月次レポートの生成・送信条件</p>
            </div>
          </div>
          <dl className="accounting-dl">
            <div>
              <dt>実行タイミング</dt>
              <dd>毎月 第3営業日 09:00（JST）</dd>
            </div>
            <div>
              <dt>今月の実行日</dt>
              <dd>{schedule.thirdBusinessDayThisMonth || '—'}</dd>
            </div>
            <div>
              <dt>Google Drive</dt>
              <dd>
                <span className={drive.configured ? 'accounting-good-badge' : 'accounting-warning-badge'}>
                  {drive.configured ? '設定済み' : '未設定'}
                </span>
              </dd>
            </div>
          </dl>
          {!drive.configured && (
            <p className="accounting-note is-warning">
              自動送信には ACCOUNTING_DRIVE_FOLDER_ID と Workspace 委任が必要です。
            </p>
          )}
        </section>
      </div>

      <section className="accounting-panel accounting-history">
        <div className="accounting-panel-title">
          <span className="accounting-panel-icon" aria-hidden="true">↺</span>
          <div>
            <h2>実行履歴</h2>
            <p>直近のレポート生成・Drive 送信結果</p>
          </div>
        </div>
        {runs.length === 0 ? (
          <p className="accounting-empty">まだレポートはありません。</p>
        ) : (
          <div className="accounting-runs">
            {runs.map((run) => (
              <article key={run.id} className="accounting-run-card">
                <div className="accounting-run-head">
                  <button
                    type="button"
                    className="accounting-history-month"
                    onClick={() => selectHistoryMonth(run.yearMonth)}
                  >
                    {formatYearMonthJa(run.yearMonth)}
                  </button>
                  <StatusBadge status={run.status} />
                  <span className="accounting-muted">{formatDateTime(run.createdAt)}</span>
                </div>
                <div className="accounting-history-counts">
                  <span>売上 {run.counts?.sales ?? 0}</span>
                  <span>決済 {run.counts?.payments ?? 0}</span>
                  <span>入金 {run.counts?.payouts ?? 0}</span>
                  <span>返金 {run.counts?.refunds ?? 0}</span>
                </div>
                {run.driveError && <p className="accounting-note is-error">{run.driveError}</p>}
                {canExport && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={!drive.configured || Boolean(busy)}
                    onClick={() => handleDriveUpload(run.id)}
                  >
                    {busy === `drive-${run.id}` && <InlineSpinner />}
                    Drive 再送
                  </button>
                )}
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminAccounting;
